// src/components/Dashboard/PlayerDashboard/ProgramSelectorBar.jsx
// Program selector for player dashboard - allows filtering by program or viewing all

import React, { useState } from 'react';
import { 
  FaChevronDown, FaCheck, FaUserCircle, 
  FaExternalLinkAlt, FaLayerGroup 
} from 'react-icons/fa';
import styles from './ProgramSelectorBar.module.css';

const SPORT_ICONS = {
  'Soccer': '⚽',
  'Basketball': '🏀',
  'Football': '🏈',
  'Volleyball': '🏐',
  'Baseball': '⚾',
  'Tennis': '🎾',
  'Hockey': '🏒',
  'Rugby': '🏉',
  'Lacrosse': '🥍',
  'Swimming': '🏊',
  'Track': '🏃',
  'Golf': '⛳',
  'default': '🏆'
};

const getSportIcon = (sport) => {
  return SPORT_ICONS[sport] || SPORT_ICONS.default;
};

const ProgramSelectorBar = ({ programs, selectedProgramId, onSelectProgram }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  if (!programs || programs.length === 0) {
    return null;
  }

  const isAllSelected = selectedProgramId === 'all';
  const selectedProgram = isAllSelected 
    ? null 
    : programs.find(p => p._id === selectedProgramId) || programs[0];

  const handleProgramSelect = (programId) => {
    onSelectProgram(programId);
    setShowDropdown(false);
  };

  return (
    <div className={styles.container}>
      {/* Program Selector Dropdown */}
      <div className={styles.selectorWrapper}>
        <div className={styles.programSelector}>
          <button 
            className={styles.selectorButton}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className={styles.programInfo}>
              <span className={styles.programIcon}>
                {isAllSelected ? (
                  <FaLayerGroup />
                ) : selectedProgram?.image ? (
                  <img src={selectedProgram.image} alt="" />
                ) : (
                  getSportIcon(selectedProgram?.sport)
                )}
              </span>
              <div className={styles.programDetails}>
                <span className={styles.programName}>
                  {isAllSelected ? 'All Programs' : selectedProgram?.name || 'Select Program'}
                </span>
                <span className={styles.programRole}>
                  <FaUserCircle className={styles.roleIcon} />
                  {isAllSelected ? `${programs.length} programs` : 'Player'}
                </span>
              </div>
            </div>
            <FaChevronDown className={`${styles.dropdownIcon} ${showDropdown ? styles.open : ''}`} />
          </button>
          
          {showDropdown && (
            <div className={styles.dropdown}>
              {/* All Programs Option */}
              <button
                className={`${styles.dropdownOption} ${isAllSelected ? styles.selected : ''}`}
                onClick={() => handleProgramSelect('all')}
              >
                <span className={styles.optionIcon}>
                  <FaLayerGroup />
                </span>
                <div className={styles.optionDetails}>
                  <span className={styles.optionName}>All Programs</span>
                  <span className={styles.optionRole}>{programs.length} programs</span>
                </div>
                {isAllSelected && <FaCheck className={styles.checkIcon} />}
              </button>

              <div className={styles.dropdownDivider}></div>

              {programs.map((program) => {
                const isSelected = program._id === selectedProgramId;
                
                return (
                  <button
                    key={program._id}
                    className={`${styles.dropdownOption} ${isSelected ? styles.selected : ''}`}
                    onClick={() => handleProgramSelect(program._id)}
                  >
                    <span className={styles.optionIcon}>
                      {program.image ? <img src={program.image} alt="" /> : getSportIcon(program.sport)}
                    </span>
                    <div className={styles.optionDetails}>
                      <span className={styles.optionName}>{program.name}</span>
                      <span className={styles.optionRole}>{program.clubName || 'Club'}</span>
                    </div>
                    {isSelected && <FaCheck className={styles.checkIcon} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* View Program Link */}
        {selectedProgramId && selectedProgramId !== 'all' && (
          <a
            href={`${window.location.origin}/program/${selectedProgramId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.viewLink}
            title="View Program Page"
          >
            <FaExternalLinkAlt />
          </a>
        )}
      </div>

      {/* Right side: Program Count */}
      <div className={styles.rightSection}>
        <div className={styles.programCount}>
          {programs.length} {programs.length === 1 ? 'Program' : 'Programs'} Enrolled
        </div>
      </div>
    </div>
  );
};

export default ProgramSelectorBar;



